import { MAX_AUTOMATIC_BACKUP_BYTES } from "../localStore/automaticBackupCrypto";
import { canonical } from "./cryptoV2";

export interface BackupDrive {
  allocateId(): Promise<string>;
  read(id: string): Promise<unknown | null>;
  create(id: string, value: unknown, kind: "automatic" | "recovery"): Promise<void>;
  remove(id: string): Promise<void>;
}
const fileId = (id: string) => {
  if (!/^[\w-]{1,128}$/.test(id)) throw new Error("sync-backup-file-id");
  return id;
};
export class GoogleBackupDrive implements BackupDrive {
  constructor(
    private readonly token: () => Promise<string>,
    private readonly scope: string,
    private readonly signal?: AbortSignal,
    private readonly network: typeof fetch = fetch,
  ) {}
  private async request(url: string, init: RequestInit = {}, missing = false): Promise<unknown | null> {
    const accessToken = await this.token();
    const signal = this.signal
      ? AbortSignal.any([this.signal, AbortSignal.timeout(90000)])
      : AbortSignal.timeout(90000);
    let response: Response;
    try {
      response = await this.network.call(globalThis, url, {
        ...init,
        signal,
        headers: { ...init.headers, Authorization: `Bearer ${accessToken}` },
        cache: "no-store",
        redirect: "error",
      });
    } catch {
      throw new Error(this.signal?.aborted ? "sync-backup-cancelled" : "sync-drive-request-failed");
    }
    if (missing && response.status === 404) return null;
    if (response.status === 204) return {};
    if (response.status === 401) throw new Error("sync-google-reconnect");
    if (response.status === 409) throw new Error("sync-drive-exists");
    if (response.status === 429) throw new Error("sync-rate-limit");
    const reader = response.body?.getReader();
    if (!reader) throw new Error("sync-backup-format");
    const parts: Uint8Array[] = [];
    let length = 0;
    try {
      while (true) {
        const part = await reader.read();
        if (part.done) break;
        length += part.value.length;
        if (length > MAX_AUTOMATIC_BACKUP_BYTES) {
          await reader.cancel();
          throw new Error("sync-backup-limit");
        }
        parts.push(part.value);
      }
    } finally {
      reader.releaseLock();
    }
    const data = new Uint8Array(length);
    let at = 0;
    for (const part of parts) {
      data.set(part, at);
      at += part.length;
    }
    let value: unknown;
    try {
      value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(data));
    } catch {
      throw new Error("sync-backup-format");
    }
    if (!response.ok) {
      const reason = (value as { error?: { errors?: { reason?: string }[] } })?.error?.errors?.[0]?.reason;
      if (reason === "storageQuotaExceeded") throw new Error("sync-drive-quota");
      throw new Error(response.status === 403 ? "sync-drive-denied" : "sync-drive-request-failed");
    }
    return value;
  }
  async allocateId() {
    const value = (await this.request(
      "https://www.googleapis.com/drive/v3/files/generateIds?count=1&space=appDataFolder&type=files",
    )) as { ids?: string[] };
    return fileId(value.ids?.[0] ?? "");
  }
  read(id: string) {
    return this.request(`https://www.googleapis.com/drive/v3/files/${fileId(id)}?alt=media`, {}, true);
  }
  async create(id: string, value: unknown, kind: "automatic" | "recovery") {
    const body = canonical(value);
    if (new TextEncoder().encode(body).length > MAX_AUTOMATIC_BACKUP_BYTES) throw new Error("sync-backup-limit");
    const metadata = {
      id: fileId(id),
      name: `classroom-backup-${id}`,
      parents: ["appDataFolder"],
      mimeType: "application/json",
      appProperties: { cdBackup: "1", scope: this.scope, kind },
    };
    const boundary = `backup_${crypto.randomUUID()}`;
    const content = `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n--${boundary}\r\nContent-Type: application/json\r\n\r\n${body}\r\n--${boundary}--\r\n`;
    const result = (await this.request(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id",
      { method: "POST", headers: { "Content-Type": `multipart/related; boundary=${boundary}` }, body: content },
    )) as { id?: string };
    if (result.id !== id) throw new Error("sync-backup-file-id");
  }
  async remove(id: string) {
    const metadata = (await this.request(
      `https://www.googleapis.com/drive/v3/files/${fileId(id)}?fields=id,appProperties`,
      {},
      true,
    )) as { id: string; appProperties?: Record<string, string> } | null;
    if (!metadata) return;
    if (
      metadata.id !== id ||
      metadata.appProperties?.cdBackup !== "1" ||
      metadata.appProperties.scope !== this.scope ||
      metadata.appProperties.kind !== "automatic"
    )
      throw new Error("sync-backup-protected");
    await this.request(`https://www.googleapis.com/drive/v3/files/${fileId(id)}`, { method: "DELETE" }, true);
  }
}
